import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, API_BASE } from '../lib/auth';
import { Button, Input, Select, Pill, PageHeader } from '../lib/ui';

import DiscountsTab from '../components/DiscountsTab';
import DeliveryBlackoutsTab from '../components/DeliveryBlackoutsTab';
import SitePreviewTab from '../components/SitePreviewTab';

const ADMIN_ROLES = ['admin', 'administrator', 'owner', 'superadmin'];

// ── Shared helpers (unchanged) ────────────────────────────────────────────────
function clampInt(v) {
  const n = Number.parseInt(String(v ?? '').replace(/[^\d-]/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
}
function clampPrice(v) {
  const n = Number.parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  if (Number.isNaN(n)) return 0;
  return Math.max(0, n);
}
function formatGBP(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '0.00';
  return x.toFixed(2);
}
function getStockModel(p) {
  if (Object.prototype.hasOwnProperty.call(p, 'stockGrams')) return 'grams';
  if (Object.prototype.hasOwnProperty.call(p, 'quantity')) return 'units';
  return 'none';
}
function normalizeCategory(s) { return String(s || '').trim().toLowerCase(); }
function splitCSV(s) {
  return String(s || '').split(',').map(x => x.trim()).filter(Boolean);
}

// ── CSV export (Products + Boxes, for sharing a price list) ─────────────────
function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function rowsToCSV(headers, rows) {
  const lines = [headers.map(csvCell).join(',')];
  rows.forEach(r => lines.push(headers.map(h => csvCell(r[h])).join(',')));
  return lines.join('\r\n');
}
function downloadCSV(filename, content) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Shared primitives (unchanged) ────────────────────────────────────────────
const PriceInput = ({ value, onChange, disabled, invalid, style }) => {
  const [local, setLocal] = useState(formatGBP(value));
  const isFocused = useRef(false);
  useEffect(() => { if (!isFocused.current) setLocal(formatGBP(value)); }, [value]);
  return (
    <Input
      disabled={disabled} inputMode="decimal" value={local} invalid={invalid} style={style}
      onFocus={() => { isFocused.current = true; }}
      onChange={(e) => {
        const val = e.target.value; setLocal(val);
        const parsed = Number.parseFloat(val.replace(/[^\d.]/g, ''));
        if (!Number.isNaN(parsed)) onChange(parsed);
      }}
      onBlur={() => {
        isFocused.current = false;
        const p = clampPrice(local); setLocal(formatGBP(p)); onChange(p);
      }}
    />
  );
};


const th ={ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: 'var(--muted)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
const label = { fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.03em' };

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCTS TAB - everything from the original file, unchanged, just wrapped
// ══════════════════════════════════════════════════════════════════════════════
function validateProductRow(next) {
  const errors = {};
  const price = Number(next.price);
  if (!Number.isFinite(price) || price < 0) errors.price = 'Invalid price';
  const s = Number(next.stock);
  if (!Number.isFinite(s) || !Number.isInteger(s)) errors.stock = 'Invalid stock';
  if (!String(next.name || '').trim()) errors.name = 'Name is required';
  return errors;
}

function CreateProductPanel({ isAdmin, onCreated, categories }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('1.00');
  const [enabled, setEnabled] = useState(true);
  const [stockValue, setStockValue] = useState('0');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [stockModel, setStockModel] = useState('grams');

  const reset = () => {
    setName(''); setPrice('1.00'); setEnabled(true);
    setStockValue('0'); setDescription(''); setImage('');
    setCategory(''); setCustomCategory('');
    setStockModel('grams'); // add this
    setErr('');
  };

  const create = async () => {
    setErr('');
    const nm = String(name || '').trim();
    if (!nm) return setErr('Name is required');
    const pr = clampPrice(price);
    const resolvedCategory = category === '__custom' ? normalizeCategory(customCategory) : normalizeCategory(category);
    if (!resolvedCategory) return setErr('Category is required');
    const payload = {
      name: nm, price: pr, category: resolvedCategory, enabled: !!enabled,
      stock: clampInt(stockValue),   // ← single field, done
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(image.trim()        ? { image: image.trim() }             : {}),
    };
    setCreating(true);
    try {
      const r = await api.post('/products', payload);
      onCreated?.(r?.data?.product || r?.data);
      reset(); setOpen(false);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to create product'); }
    finally { setCreating(false); }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', overflow: 'hidden' }}>
      <div style={{ padding: 12, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <b>Add product</b>
          {!isAdmin && <Pill tone="warn">Admin required</Pill>}
        </div>
        <Button onClick={() => setOpen(o => !o)} disabled={!isAdmin}>{open ? 'Close' : 'New product'}</Button>
      </div>
      {open && (
        <div style={{ padding: 12, background: 'var(--panel)' }}>
          {err && <div style={{ marginBottom: 10, padding: 10, borderRadius: 'var(--r-lg)', background: '#ffecec', border: '1px solid #f5c2c7', color: '#b30000' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 220px 180px', gap: 10 }}>
            <div><div style={label}>Name</div><Input value={name} onChange={e => setName(e.target.value)} /></div>
            <div><div style={label}>Price (£)</div><Input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" /></div>
            <div>
              <div style={label}>Category</div>
              <Select value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Select category…</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__custom">Add new…</option>
              </Select>
              {category === '__custom' && <Input style={{ marginTop: 8 }} value={customCategory} onChange={e => setCustomCategory(e.target.value)} />}
            </div>
            <div>
              <div style={label}>Visibility</div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
                <span>Shown on website</span>
              </label>
            </div>
            <div><div style={label}>Description</div><Input value={description} onChange={e => setDescription(e.target.value)} /></div>
            <div><div style={label}>Image folder</div><Input value={image} onChange={e => setImage(e.target.value)} /></div>
            <div><div style={label}>Initial stock</div><Input value={stockValue} onChange={e => setStockValue(e.target.value)} /></div>
            <div>
              <div style={label}>Stock type</div>
              <Select value={stockModel} onChange={e => setStockModel(e.target.value)}>
                <option value="grams">Grams (stockGrams)</option>
                <option value="units">Units (quantity)</option>
              </Select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={create} disabled={creating}>Create</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function getStock(p) {
  if (p?.stock != null) return Number(p.stock);
  if (p?.stockGrams != null) return Number(p.stockGrams);
  if (p?.quantity != null) return Number(p.quantity);
  return 0;
}

// ── Product photo manager ─────────────────────────────────────────────────────
// Uploads go through the server's existing /upload-images route; the resulting
// URLs are stored on product.images (first = lead photo on the website).
const publicImageUrl = (u) =>
  String(u).startsWith('/api') ? API_BASE + String(u).slice(4) : u;

function ProductPhotos({ product, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const fileRef = useRef(null);
  const images = Array.isArray(product.images) ? product.images : [];

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(publicImageUrl(url)).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  const saveImages = async (next) => {
    await api.post(`/products/${product.id}`, { images: next });
    onChanged(next);
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      const r = await api.post('/upload-images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploaded = r.data?.uploaded || [];
      const urls = uploaded.map((x) => x.url).filter(Boolean);
      if (!urls.length) throw new Error('No files stored');
      await saveImages([...images, ...urls]);
      const warnings = uploaded.filter((x) => x.warning);
      if (warnings.length) {
        setErr(`${warnings.length} photo(s) uploaded without compression (unsupported format, e.g. HEIC) — try re-exporting as JPEG.`);
      }
    } catch {
      setErr('Upload failed — check the file type and try again');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = async (url) => {
    if (!window.confirm('Remove this photo from the product?')) return;
    try { await saveImages(images.filter((u) => u !== url)); }
    catch { setErr('Failed to remove photo'); }
  };

  const makeLead = async (url) => {
    try { await saveImages([url, ...images.filter((u) => u !== url)]); }
    catch { setErr('Failed to reorder'); }
  };

  return (
    <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--r-lg)', border: '1px dashed var(--border)', background: 'var(--panel)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>📷 Website photos</div>
        <Button onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : '+ Add photos'}
        </Button>
        <input
          ref={fileRef} type="file" accept="image/*" multiple
          style={{ display: 'none' }} onChange={onUpload}
        />
        {err && <span style={{ fontSize: 12, color: '#ef4444' }}>{err}</span>}
      </div>

      {images.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
          No uploaded photos — the website is using its built-in photo set for this product.
          Upload photos here to take over what customers see.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          {images.map((u, i) => (
            <div key={u} style={{ width: 110, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden', background: 'var(--bg)' }}>
              <div style={{ position: 'relative' }}>
                <img
                  src={publicImageUrl(u)} alt=""
                  style={{ width: '100%', height: 78, objectFit: 'cover', display: 'block' }}
                />
                {i === 0 && (
                  <span style={{ position: 'absolute', top: 4, left: 4, fontSize: 9, fontWeight: 700, background: '#10b981', color: '#fff', borderRadius: 'var(--r-sm)', padding: '1px 6px' }}>
                    LEAD
                  </span>
                )}
              </div>
              <div style={{ display: 'flex' }}>
                {i !== 0 && (
                  <button
                    onClick={() => makeLead(u)}
                    title="Use as the main photo"
                    style={{ flex: 1, fontSize: 10, padding: '4px 0', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text)' }}
                  >★ Lead</button>
                )}
                <button
                  onClick={() => copyImageUrl(u)}
                  title="Copy image URL"
                  style={{ flex: 1, fontSize: 10, padding: '4px 0', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text)' }}
                >{copiedUrl === u ? '✓ Copied' : '🔗 Copy'}</button>
                <button
                  onClick={() => removeImage(u)}
                  title="Remove photo"
                  style={{ flex: 1, fontSize: 10, padding: '4px 0', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }}
                >✕ Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductsTab({ isAdmin }) {
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState('all');
  const [showHidden, setShowHidden] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [rowOpen, setRowOpen] = useState({});
  const [banner, setBanner] = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const bannerTimerRef = useRef(null);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach(p => p.category && set.add(p.category.toLowerCase()));
    return Array.from(set).sort();
  }, [products]);

  const flashBanner = (msg) => {
    setBanner(msg);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(''), 2200);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/products');
      setProducts(Array.isArray(r?.data) ? r.data : []);
      setDrafts({});
    } catch { setLoadErr('Failed to load products'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const mergedRow = (p) => {
    const d = drafts[String(p.id)];
    return d ? { ...p, ...d, id: p.id } : p;
  };

  const setDraft = (id, updater) => {
    setDrafts(prev => {
      const base = products.find(x => String(x.id) === String(id));
      const current = prev[id] ? { ...base, ...prev[id] } : { ...base };
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      if (JSON.stringify(next) === JSON.stringify(base)) {
        const { [id]: _, ...rest } = prev; return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const revertRow = (id) => setDrafts(prev => { const { [id]: _, ...rest } = prev; return rest; });

  const dirtyIds = useMemo(() => Object.keys(drafts), [drafts]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter(p => {
      if (!showHidden && p.enabled === false) return false;
      const m = getStockModel(p);
      if (mode === 'grams' && m !== 'grams') return false;
      if (mode === 'units' && m !== 'units') return false;
      return `${p.id} ${p.name} ${p.category}`.toLowerCase().includes(term);
    }).sort((a, b) => a.id - b.id);
  }, [products, q, mode, showHidden]);

  const rowErrors = useMemo(() => {
    const out = {};
    for (const id of dirtyIds) {
      const base = products.find(x => String(x.id) === id);
      const errs = validateProductRow(mergedRow(base));
      if (Object.keys(errs).length) out[id] = errs;
    }
    return out;
  }, [drafts, products]);

  const hasErrors = Object.keys(rowErrors).length > 0;

  const saveRow = async (p) => {
    const id = String(p.id);
    const next = mergedRow(p);
    try {
      await api.post(`/products/${id}`, next);
      setProducts(list => list.map(x => String(x.id) === id ? next : x));
      revertRow(id); flashBanner(`Saved #${id}`);
    } catch { flashBanner('Error saving'); }
  };

  const saveAll = async () => {
    setSavingAll(true);
    await Promise.allSettled(dirtyIds.map(id => {
      const base = products.find(x => String(x.id) === id);
      return api.post(`/products/${id}`, mergedRow(base));
    }));
    load(); setSavingAll(false);
  };

  const incStock = (p, delta) => {
    setDraft(p.id, { stock: getStock(mergedRow(p)) + delta });
  };

  const setVisibility = async (p, enabled) => {
    await api.patch(`/products/${p.id}/visibility`, { enabled });
    setProducts(l => l.map(x => x.id === p.id ? { ...x, enabled } : x));
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setProducts(l => l.filter(x => x.id !== p.id));
      flashBanner(`Deleted #${p.id}`);
    } catch { flashBanner('Failed to delete product'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 300, flex: '1 1 180px' }} />
        <div className="button-group" style={{ flexShrink: 0 }}>
          {[['all', 'All'], ['grams', 'By weight'], ['units', 'By unit']].map(([val, lbl]) => (
            <button key={val} className={mode === val ? 'active' : ''} onClick={() => setMode(val)}>{lbl}</button>
          ))}
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--muted)', flexShrink: 0 }}>
          <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} /> Show hidden
        </label>
        <Button variant="primary" onClick={saveAll} disabled={!dirtyIds.length || hasErrors || savingAll} style={{ marginLeft: 'auto' }}>
          {savingAll ? 'Saving…' : `Save All (${dirtyIds.length})`}
        </Button>
      </div>

      {banner && <div style={{ padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 10, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#15803d' }}>{banner}</div>}

      <CreateProductPanel isAdmin={isAdmin} categories={categories} onCreated={load} />

      <div style={{ marginTop: 20, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>ID</th><th style={th}>Product</th><th style={th}>Status</th>
              <th style={th}>Price (£)</th><th style={th}>Stock</th>
              <th style={th}>Quick</th><th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const id = String(p.id);
              const next = mergedRow(p);
              const m = getStockModel(p);
              const dirty = !!drafts[id];
              const errs = rowErrors[id] || {};
              const stockVal = getStock(next);
              const isSoon = stockVal < 0;

              return (
                <React.Fragment key={id}>
                  <tr style={{ background: dirty ? '#fffdf3' : 'var(--panel)', borderBottom: '1px solid var(--border)' }}>
                    <td style={td}><Pill tone="ok">#{id}</Pill></td>
                    <td style={td}>
                      <Input
                        value={next.name || ''}
                        onChange={e => setDraft(id, { name: e.target.value })}
                        invalid={!String(next.name || '').trim()}
                        style={{ fontWeight: 600, minWidth: 160 }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{p.category}</div>
                      {isSoon && <div style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700 }}>COMING SOON</div>}
                      {next.harvestHighlight && <div style={{ color: '#10b981', fontSize: 10, fontWeight: 700 }}>★ THIS WEEK'S HARVEST</div>}
                    </td>
                    <td style={td}>{p.enabled ? <Pill tone="ok">Shown</Pill> : <Pill tone="warn">Hidden</Pill>}</td>
                    <td style={td}><PriceInput value={next.price} onChange={v => setDraft(id, { price: v })} invalid={errs.price} /></td>
                    <td style={td}>
                      <Input
                        value={stockVal}
                        onChange={e => setDraft(id, { stock: clampInt(e.target.value) })}
                        style={{ color: isSoon ? '#f59e0b' : 'inherit', fontWeight: isSoon ? 'bold' : 'normal' }}
                      />
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button onClick={() => incStock(p, m === 'grams' ? 50 : 1)}>{m === 'grams' ? '+50g' : '+1'}</Button>
                        <Button onClick={() => setDraft(id, { stock: -1 })}>Soon</Button>
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button onClick={() => setRowOpen(prev => ({ ...prev, [id]: !prev[id] }))}>{rowOpen[id] ? 'Close' : 'Details'}</Button>
                        <Button variant="primary" onClick={() => saveRow(p)} disabled={!dirty || !!Object.keys(errs).length}>Save</Button>
                        <Button onClick={() => setVisibility(p, !p.enabled)}>{p.enabled ? 'Hide' : 'Show'}</Button>
                        <Button variant="danger" onClick={() => deleteProduct(p)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                  {rowOpen[id] && (
                    <tr style={{ background: 'var(--bg)' }}>
                      <td colSpan={7} style={{ padding: 15, borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 2fr', gap: '15px' }}>
                          <div>
                            <div style={label}>Category</div>
                            <Input value={next.category} onChange={e => setDraft(id, { category: e.target.value })} />
                          </div>
                          <div>
                            <div style={label}>Description</div>
                            <textarea
                              style={{ width: '100%', height: 60, padding: '8px 10px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', background: 'var(--panel)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }}
                              value={next.description || ''}
                              onChange={e => setDraft(id, { description: e.target.value })}
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--r-lg)', border: '1px dashed var(--border)', background: 'var(--panel)' }}>
                          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
                              <input
                                type="checkbox"
                                checked={!!next.harvestHighlight}
                                onChange={e => setDraft(id, { harvestHighlight: e.target.checked })}
                              />
                              ★ Feature in "This week's harvest" on the website
                            </label>
                            <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                              <Input
                                placeholder='Note shown on site (e.g. "Cut fresh Friday morning")'
                                value={next.harvestNote || ''}
                                onChange={e => setDraft(id, { harvestNote: e.target.value })}
                                disabled={!next.harvestHighlight}
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
                            Featured products appear in the homepage harvest strip. Leave the note blank for the default "Harvesting this week".
                          </div>
                        </div>
                        <ProductPhotos
                          product={p}
                          onChanged={(imgs) => setProducts(list => list.map(x => x.id === p.id ? { ...x, images: imgs } : x))}
                        />
                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button onClick={() => revertRow(id)} variant="danger" style={{ fontSize: 11, padding: '4px 8px' }}>Discard Changes</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>
            No products match your filters.
          </div>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BOXES TAB
// ══════════════════════════════════════════════════════════════════════════════
function validateBoxRow(next) {
  const errors = {};
  if (!String(next.name || '').trim()) errors.name = 'Name is required';
  const price = Number(next.price);
  if (!Number.isFinite(price) || price < 0) errors.price = 'Invalid price';
  if (next.originalPrice !== '' && next.originalPrice != null) {
    const op = Number(next.originalPrice);
    if (!Number.isFinite(op) || op < 0) errors.originalPrice = 'Invalid original price';
  }
  return errors;
}

function CreateBoxPanel({ isAdmin, onCreated }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('9.99');
  const [originalPrice, setOriginalPrice] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [grams, setGrams] = useState('');
  const [image, setImage] = useState('');
  const [badges, setBadges] = useState('');
  const [contents, setContents] = useState('');
  const [enabled, setEnabled] = useState(true);

  const reset = () => {
    setName(''); setPrice('9.99'); setOriginalPrice(''); setTagline('');
    setDescription(''); setGrams(''); setImage(''); setBadges('');
    setContents(''); setEnabled(true); setErr('');
  };

  const create = async () => {
    setErr('');
    const nm = String(name || '').trim();
    if (!nm) return setErr('Name is required');
    const pr = clampPrice(price);
    const payload = {
      name: nm, price: pr, enabled: !!enabled,
      ...(originalPrice.trim() ? { originalPrice: clampPrice(originalPrice) } : {}),
      ...(tagline.trim()       ? { tagline: tagline.trim() }                  : {}),
      ...(description.trim()   ? { description: description.trim() }          : {}),
      ...(grams.trim()         ? { grams: grams.trim() }                      : {}),
      ...(image.trim()                 ? { image: image.trim() }                              : {}),
      badges:   splitCSV(badges),
      contents: splitCSV(contents),
    };
    setCreating(true);
    try {
      const r = await api.post('/boxes', payload);
      onCreated?.(r?.data?.box || r?.data);
      reset(); setOpen(false);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to create box'); }
    finally { setCreating(false); }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', overflow: 'hidden' }}>
      <div style={{ padding: 12, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <b>Add box</b>
          {!isAdmin && <Pill tone="warn">Admin required</Pill>}
        </div>
        <Button onClick={() => setOpen(o => !o)} disabled={!isAdmin}>{open ? 'Close' : 'New box'}</Button>
      </div>
      {open && (
        <div style={{ padding: 12, background: 'var(--panel)' }}>
          {err && <div style={{ marginBottom: 10, padding: 10, borderRadius: 'var(--r-lg)', background: '#ffecec', border: '1px solid #f5c2c7', color: '#b30000' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 1fr', gap: 10, marginBottom: 10 }}>
            <div><div style={label}>Name</div><Input value={name} onChange={e => setName(e.target.value)} /></div>
            <div><div style={label}>Price (£)</div><Input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" /></div>
            <div><div style={label}>Was (£) - optional</div><Input value={originalPrice} onChange={e => setOriginalPrice(e.target.value)} inputMode="decimal" placeholder="e.g. 12.50" /></div>
            <div><div style={label}>Grams label</div><Input value={grams} onChange={e => setGrams(e.target.value)} placeholder="~250g mixed" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div><div style={label}>Tagline</div><Input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Not sure where to start? Let us decide." /></div>
            <div><div style={label}>Image path</div><Input value={image} onChange={e => setImage(e.target.value)} placeholder="/images/products/mix/IMG_4391.JPEG" /></div>
            <div><div style={label}>Badges (comma-separated)</div><Input value={badges} onChange={e => setBadges(e.target.value)} placeholder="🌿 Today's Harvest, 💚 Best Seller" /></div>
            <div><div style={label}>Contents (comma-separated)</div><Input value={contents} onChange={e => setContents(e.target.value)} placeholder="Sweet Genovese Basil, French Salad Mix" /></div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={label}>Description</div>
            <textarea
              style={{ width: '100%', height: 60, padding: '8px 10px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', fontFamily: 'inherit', outline: 'none' }}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="A hand-picked selection of whatever is at peak freshness today…"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input type="checkbox" id="box-enabled" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <label htmlFor="box-enabled" style={{ fontSize: 13 }}>Shown on website</label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={create} disabled={creating}>Create</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BoxesTab({ isAdmin }) {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [showHidden, setShowHidden] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [rowOpen, setRowOpen] = useState({});
  const [banner, setBanner] = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const bannerTimerRef = useRef(null);

  const flashBanner = (msg) => {
    setBanner(msg);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(''), 2200);
  };

  const load = async () => {
    setLoading(true);
    try {
      // ?all=1 so admin sees disabled boxes too
      const r = await api.get('/boxes?all=1');
      setBoxes(Array.isArray(r?.data) ? r.data : []);
      setDrafts({});
    } catch { flashBanner('Failed to load boxes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const mergedRow = (b) => {
    const d = drafts[String(b.id)];
    return d ? { ...b, ...d, id: b.id } : b;
  };

  const setDraft = (id, updater) => {
    setDrafts(prev => {
      const base = boxes.find(x => String(x.id) === String(id));
      const current = prev[id] ? { ...base, ...prev[id] } : { ...base };
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      if (JSON.stringify(next) === JSON.stringify(base)) {
        const { [id]: _, ...rest } = prev; return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const revertRow = (id) => setDrafts(prev => { const { [id]: _, ...rest } = prev; return rest; });
  const dirtyIds = useMemo(() => Object.keys(drafts), [drafts]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return boxes.filter(b => {
      if (!showHidden && b.enabled === false) return false;
      return `${b.id} ${b.name}`.toLowerCase().includes(term);
    }).sort((a, b) => a.id - b.id);
  }, [boxes, q, showHidden]);

  const rowErrors = useMemo(() => {
    const out = {};
    for (const id of dirtyIds) {
      const base = boxes.find(x => String(x.id) === id);
      if (!base) continue;
      const errs = validateBoxRow(mergedRow(base));
      if (Object.keys(errs).length) out[id] = errs;
    }
    return out;
  }, [drafts, boxes]);

  const hasErrors = Object.keys(rowErrors).length > 0;

  const saveRow = async (b) => {
    const id = String(b.id);
    const next = mergedRow(b);
    // Coerce comma-string fields back to arrays if someone edited them as text
    const payload = {
      ...next,
      badges:   Array.isArray(next.badges)   ? next.badges   : splitCSV(next.badges),
      contents: Array.isArray(next.contents) ? next.contents : splitCSV(next.contents),
    };
    try {
      await api.post(`/boxes/${id}`, payload);
      setBoxes(list => list.map(x => String(x.id) === id ? payload : x));
      revertRow(id); flashBanner(`Saved box #${id}`);
    } catch { flashBanner('Error saving box'); }
  };

  const saveAll = async () => {
    setSavingAll(true);
    await Promise.allSettled(dirtyIds.map(id => {
      const base = boxes.find(x => String(x.id) === id);
      return api.post(`/boxes/${id}`, mergedRow(base));
    }));
    load(); setSavingAll(false);
  };

  const setVisibility = async (b, enabled) => {
    await api.patch(`/boxes/${b.id}/visibility`, { enabled });
    setBoxes(l => l.map(x => x.id === b.id ? { ...x, enabled } : x));
  };

  const deleteBox = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/boxes/${b.id}`);
      setBoxes(l => l.filter(x => x.id !== b.id));
      flashBanner(`Deleted box #${b.id}`);
    } catch { flashBanner('Failed to delete box'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Search boxes…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 300, flex: '1 1 180px' }} />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--muted)', flexShrink: 0 }}>
          <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} /> Show hidden
        </label>
        <Button variant="primary" onClick={saveAll} disabled={!dirtyIds.length || hasErrors || savingAll} style={{ marginLeft: 'auto' }}>
          {savingAll ? 'Saving…' : `Save All (${dirtyIds.length})`}
        </Button>
      </div>

      {banner && <div style={{ padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 10, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#15803d' }}>{banner}</div>}

      <CreateBoxPanel isAdmin={isAdmin} onCreated={load} />

      <div style={{ marginTop: 20, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>ID</th>
              <th style={th}>Box name</th>
              <th style={th}>Status</th>
              <th style={th}>Price (£)</th>
              <th style={th}>Was (£)</th>
              <th style={th}>Stock</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(b => {
              const id = String(b.id);
              const next = mergedRow(b);
              const dirty = !!drafts[id];
              const errs = rowErrors[id] || {};
              const contentsCount = (next.contents || []).length;

              return (
                <React.Fragment key={id}>
                  <tr style={{ background: dirty ? '#fffdf3' : 'var(--panel)', borderBottom: '1px solid var(--border)' }}>
                    <td style={td}><Pill tone="ok">#{id}</Pill></td>
                    <td style={td}>
                      <Input
                        value={next.name || ''}
                        onChange={e => setDraft(id, { name: e.target.value })}
                        invalid={!String(next.name || '').trim()}
                        style={{ fontWeight: 600, minWidth: 160 }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {b.grams && `${b.grams} · `}{contentsCount} item{contentsCount !== 1 ? 's' : ''} in contents
                      </div>
                    </td>
                    <td style={td}>{b.enabled ? <Pill tone="ok">Shown</Pill> : <Pill tone="warn">Hidden</Pill>}</td>
                    <td style={td}>
                      <PriceInput
                        value={next.price}
                        onChange={v => setDraft(id, { price: v })}
                        invalid={errs.price}
                        style={{ width: 90 }}
                      />
                    </td>
                    <td style={td}>
                      <PriceInput
                        value={next.originalPrice ?? ''}
                        onChange={v => setDraft(id, { originalPrice: v || null })}
                        invalid={errs.originalPrice}
                        style={{ width: 90 }}
                      />
                    </td>
                    <td style={td}>
                      {next.infiniteStock
                        ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>∞</span>
                        : <span style={{ fontSize: 13 }}>{next.stock != null ? next.stock : <span style={{ color: 'var(--muted)' }}>-</span>}</span>
                      }
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button onClick={() => setRowOpen(prev => ({ ...prev, [id]: !prev[id] }))}>
                          {rowOpen[id] ? 'Close' : 'Details'}
                        </Button>
                        <Button variant="primary" onClick={() => saveRow(b)} disabled={!dirty || !!Object.keys(errs).length}>Save</Button>
                        <Button onClick={() => setVisibility(b, !b.enabled)}>{b.enabled ? 'Hide' : 'Show'}</Button>
                        <Button variant="danger" onClick={() => deleteBox(b)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                  {rowOpen[id] && (
                    <tr style={{ background: 'var(--bg)' }}>
                      <td colSpan={7} style={{ padding: 15, borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginBottom: 12 }}>
                          <div>
                            <div style={label}>Tagline</div>
                            <Input value={next.tagline || ''} onChange={e => setDraft(id, { tagline: e.target.value })} />
                          </div>
                          <div>
                            <div style={label}>Grams label</div>
                            <Input value={next.grams || ''} onChange={e => setDraft(id, { grams: e.target.value })} placeholder="~250g mixed" />
                          </div>
                          <div>
                            <div style={label}>Description</div>
                            <textarea
                              style={{ width: '100%', height: 60, padding: '8px 10px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', background: 'var(--panel)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }}
                              value={next.description || ''}
                              onChange={e => setDraft(id, { description: e.target.value })}
                            />
                          </div>
                          <div>
                            <div style={label}>Image path</div>
                            <Input value={next.image || ''} onChange={e => setDraft(id, { image: e.target.value })} />
                          </div>
                          <div>
                            <div style={label}>Badges (comma-separated)</div>
                            <Input
                              value={Array.isArray(next.badges) ? next.badges.join(', ') : (next.badges || '')}
                              onChange={e => setDraft(id, { badges: splitCSV(e.target.value) })}
                              placeholder="🌿 Today's Harvest, 💚 Best Seller"
                            />
                          </div>
                          <div>
                            <div style={label}>Contents (comma-separated)</div>
                            <Input
                              value={Array.isArray(next.contents) ? next.contents.join(', ') : (next.contents || '')}
                              onChange={e => setDraft(id, { contents: splitCSV(e.target.value) })}
                              placeholder="Sweet Genovese Basil, French Salad Mix"
                            />
                          </div>
                          <div>
                            <div style={label}>Stock</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <Input
                                type="number"
                                min="0"
                                placeholder="e.g. 10"
                                value={next.infiniteStock ? '' : (next.stock ?? '')}
                                disabled={!!next.infiniteStock}
                                onChange={e => setDraft(id, { stock: e.target.value === '' ? null : Number(e.target.value) })}
                                style={{ width: 80 }}
                              />
                              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                                <input
                                  type="checkbox"
                                  checked={!!next.infiniteStock}
                                  onChange={e => setDraft(id, { infiniteStock: e.target.checked, stock: e.target.checked ? null : next.stock })}
                                />
                                Infinite
                              </label>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button onClick={() => revertRow(id)} variant="danger" style={{ fontSize: 11, padding: '4px 8px' }}>Discard Changes</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>
            No boxes yet - create one above.
          </div>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT PAGE - tab switcher wraps both tabs
// ══════════════════════════════════════════════════════════════════════════════
export default function ProductsPage() {
  const [role, setRole] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/auth/me');
        const r = (me?.data?.user?.role || '').toLowerCase();
        setRole(r);
        setIsAdmin(ADMIN_ROLES.includes(r));
      } catch { setIsAdmin(false); }
    })();
  }, []);

  const handleExportCSV = async () => {
    setExporting(true);
    setExportErr('');
    try {
      const [prodRes, boxRes] = await Promise.all([
        api.get('/products'),
        api.get('/boxes?all=1'),
      ]);
      const products = Array.isArray(prodRes?.data) ? prodRes.data : [];
      const boxes = Array.isArray(boxRes?.data) ? boxRes.data : [];

      const headers = ['Type', 'ID', 'Name', 'Category', 'Contents', 'Weight', 'Price (£)', 'Was Price (£)', 'Status', 'Stock', 'Description'];

      const productRows = products.map(p => ({
        Type: 'Product',
        ID: p.id,
        Name: p.name,
        Category: p.category || '',
        Contents: '',
        Weight: '',
        'Price (£)': formatGBP(p.price),
        'Was Price (£)': '',
        Status: p.enabled ? 'Shown' : 'Hidden',
        Stock: getStock(p) < 0 ? 'Coming soon' : getStock(p),
        Description: p.description || '',
      }));

      const boxRows = boxes.map(b => ({
        Type: 'Box',
        ID: b.id,
        Name: b.name,
        Category: '',
        Contents: (b.contents || []).join('; '),
        Weight: b.grams || '',
        'Price (£)': formatGBP(b.price),
        'Was Price (£)': b.originalPrice != null ? formatGBP(b.originalPrice) : '',
        Status: b.enabled ? 'Shown' : 'Hidden',
        Stock: b.infiniteStock ? 'Unlimited' : (b.stock != null ? b.stock : ''),
        Description: b.tagline || b.description || '',
      }));

      const csv = rowsToCSV(headers, [...productRows, ...boxRows]);
      const today = new Date().toISOString().slice(0, 10);
      downloadCSV(`teardrop-catalogue-${today}.csv`, csv);
    } catch {
      setExportErr('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-container products-page">
      <PageHeader
        title="Catalogue"
        subtitle="Products, boxes and what the shop shows"
        actions={(
          <>
            {exportErr && <span style={{ fontSize: 12, color: 'var(--bad-fg)' }}>{exportErr}</span>}
            <Pill tone="ok">{role || 'Loading…'}</Pill>
            <Button onClick={handleExportCSV} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </>
        )}
        tabs={[
          { key: 'products',  label: 'Products' },
          { key: 'boxes',     label: 'Boxes' },
          { key: 'discounts', label: 'Discounts' },
          { key: 'delivery',  label: 'Delivery' },
          { key: 'preview',   label: 'Site preview' },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'products'  && <ProductsTab  isAdmin={isAdmin} />}
      {activeTab === 'boxes'     && <BoxesTab     isAdmin={isAdmin} />}
      {activeTab === 'discounts' && <DiscountsTab isAdmin={isAdmin} />}
      {activeTab === 'delivery'  && <DeliveryBlackoutsTab isAdmin={isAdmin} />}
      {activeTab === 'preview'   && <SitePreviewTab />}
    </div>
  );
}